import { Button } from '@/components/form/Button';

import DaaSTable, { DaasTableColumn } from '@/components/table/DaasTable';
import { PeripheralFinanceModelTypes } from '@/services/api/contracts/financemodels';
import {
  CreateFinanceModelArgs,
  CreatePeripheralFinanceModelArgs,
  useCreateFinanceModels,
  useCreatePeripheralFinanceModels,
  useFinanceModels,
  usePeripheralFinanceModels
} from '@/services/api/hooks/financemodels';
import { useResult } from '@/services/csv/useCSVService';

import { ModalProps } from '@/components/modal/useModal';
import { useControls } from '@/components/multistep/useMultistep';
import DaasTableRowCell from '@/components/table/DaasTableRowCell';
import { useCategories, useManufacturers } from '@/services/api/hooks/catalog';
import { FC, Fragment } from 'react';
import { useBuildFullRegionMap } from '../helpers/useBuildFullRegionMap';
import { useGetKnownRegionsFromCsv } from '../helpers/useGetKnownRegionsFromCsv';
import {
  constructCSVModelIdentifier,
  constructCSVNameFromTypeAndTerm,
  filterNonIdenticalBulkModels,
  mapBulkFinanceModelTypeToModelAPIType,
  mapBulkFinanceModelTypeToPeripheralModelAPIType
} from '../helpers/utils';
import { Result } from './UploadFinanceModelStep';

const tableHeaders: DaasTableColumn[] = [
  {
    accessor: '',
    text: 'Name',
    size: '0.5fr'
  },
  {
    accessor: 'Category',
    text: 'Category',
    size: '0.7fr'
  },
  {
    accessor: 'Manufacturer',
    text: 'Manufacturer',
    size: '0.7fr'
  },
  {
    accessor: 'Term',
    text: 'Term',
    size: '0.5fr'
  },
  {
    accessor: 'Type',
    text: 'Type',
    size: '0.5fr'
  },
  {
    accessor: 'Pcms',
    subAccessor: 'type',
    text: 'Regional PCM Multiplier'
  }
];

const processPcmTableHeader = (regionCodes: string[]) => {
  const pcmHeader = tableHeaders.find(header => header.accessor === 'Pcms');
  pcmHeader!.subHeaders = [];
  regionCodes.forEach(region => pcmHeader?.subHeaders?.push(region));
};

export const ReviewFinanceModelStep: FC<ModalProps> = props => {
  const result = useResult<Result>();
  if (!result) throw Error('No result');

  const models = useFinanceModels();
  const peripheralModels = usePeripheralFinanceModels();

  const data = filterNonIdenticalBulkModels(result.data, models, peripheralModels);
  const combinedData = [...data.mainDevices, ...data.peripherals];

  const [categories] = useCategories();
  const [manufacturers] = useManufacturers();

  const createFinanceModels = useCreateFinanceModels();
  const createPeripheralFinanceModels = useCreatePeripheralFinanceModels();

  const regionCodes = useGetKnownRegionsFromCsv();
  const fullRegionMap = useBuildFullRegionMap();

  const onFinish = async () => {
    const mainDevicePayload: CreateFinanceModelArgs[] = [];

    data.mainDevices.forEach(incoming => {
      const category = categories.find(cat => cat.name.toLowerCase() === incoming.Category.toLowerCase());
      const manufacturer = manufacturers.find(man => man.name.toLowerCase() === incoming.Manufacturer.toLowerCase());

      if (!(category && manufacturer)) throw new Error('category / manufacturer not found');

      incoming.Pcms.forEach(pcm => {
        const regionId = fullRegionMap[pcm.type];

        const payload = {
          categoryId: category.id,
          manufacturerId: manufacturer.id,
          value: 120,
          type: mapBulkFinanceModelTypeToModelAPIType(incoming.Type),
          term: Number(incoming.Term),
          pcmFactor: pcm.value,
          regionId
        };

        mainDevicePayload.push(payload);
      });
    });

    const peripheralFinanceModelsPayload: CreatePeripheralFinanceModelArgs[] = [];
    data.peripherals.forEach(incoming => {
      incoming.Pcms.forEach(pcm => {
        const regionId = fullRegionMap[pcm.type];

        const payload = {
          value: 120,
          type: mapBulkFinanceModelTypeToPeripheralModelAPIType(
            incoming.Type
          ) as unknown as PeripheralFinanceModelTypes,
          pcmFactor: pcm.value,
          term: Number(incoming.Term),
          regionId
        };

        peripheralFinanceModelsPayload.push(payload);
      });
    });

    await createFinanceModels.mutateAsync(mainDevicePayload);
    await createPeripheralFinanceModels.mutateAsync(peripheralFinanceModelsPayload);

    props.close();
  };

  const { previous } = useControls();

  processPcmTableHeader(regionCodes);

  return (
    <div className="flex flex-col gap-2">
      <p className="font-medium text-md">
        {combinedData.length === 0
          ? 'No new finance models to upload have been found'
          : 'Please review the finance models below and press apply to upload'}
      </p>

      {combinedData.length > 0 && (
        <div className="flex flex-col gap-5 max-h-[40vh] overflow-auto">
          <DaaSTable headers={tableHeaders} className="w-fit">
            {combinedData.map(t => {
              return (
                <Fragment key={constructCSVModelIdentifier(t)}>
                  <DaasTableRowCell>{constructCSVNameFromTypeAndTerm(t.Type, t.Term)}</DaasTableRowCell>
                  <DaasTableRowCell>{t.Category}</DaasTableRowCell>
                  <DaasTableRowCell>{t.Manufacturer}</DaasTableRowCell>
                  <DaasTableRowCell>{t.Term ? `${t.Term} Years` : ''} </DaasTableRowCell>
                  <DaasTableRowCell>{t.Type}</DaasTableRowCell>
                  <DaasTableRowCell className="!p-0 flex [&>*:not(:last-child)]:border-r">
                    {t.Pcms.map(item => (
                      <div
                        key={`${constructCSVModelIdentifier(t)}-${item.type}-${item.value}`}
                        className="h-full border-[#DDDDDD] flex flex-1 items-center px-3 py-2 justify-between"
                      >
                        {item.value ? +item.value.toFixed(6) : '-'}
                      </div>
                    ))}
                  </DaasTableRowCell>
                </Fragment>
              );
            })}
          </DaaSTable>
        </div>
      )}
      <div className="flex justify-between">
        <Button onClick={previous}>Previous</Button>
        <Button loading={false} onClick={onFinish}>
          {combinedData.length == 0 ? 'Continue' : 'Apply'}
        </Button>
      </div>
    </div>
  );
};
